import { useContext, useRef } from "react";
import type { FC, Ref } from "react";

import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { MoveCoursesToGroupingModal } from "./MoveCoursesToGroupingModal";

import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import type { MenuChangeEvent, MenuInstance } from "mds/components/Menu";
import { Menu, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, ChevronDownIcon, CopySendArrowIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCoursesWithGrouping } from "store/selectors/section";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";

type AddCourseOptionMenuProps = {
  groupingId: string;
};

export const AddCourseOptionMenu: FC<AddCourseOptionMenuProps> = ({ groupingId }) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const coursesWithGrouping = useAppSelector(selectCoursesWithGrouping);
  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    hoverableContext?.setShouldStayOpen?.(event.open);
  };

  const handleAddNewCourse = () => {
    showAddCourseModal(groupingId);
    menuRef.current?.closeMenu();
  };

  const handleMoveExistingCourses = () => {
    showConfirmationModal({
      title: t("home.courses.move_courses_to_grouping_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <MoveCoursesToGroupingModal
          courses={coursesWithGrouping}
          groupingId={groupingId}
          onClose={onClose}
        />
      ),
    });

    menuRef.current?.closeMenu();
  };

  const MenuButton = (
    <SquareButton
      eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course to Folder`}
      kind="secondary"
      size="s"
      title={t("common.add.course")}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <IconText
        iconEnd={<ChevronDownIcon />}
        iconStart={<AddPlusIcon />}
        text={t("common.add.course")}
      />
    </SquareButton>
  );

  return (
    <Menu
      instanceRef={menuRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onMenuChange={onMenuChange}
    >
      <MenuItem onClick={handleAddNewCourse}>
        <IconText iconStart={<AddPlusIcon />} text={t("home.courses.add_course_heading")} />
      </MenuItem>

      <MenuItem onClick={handleMoveExistingCourses}>
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("sidebar_options_menu.move_existing_courses_here")}
        />
      </MenuItem>
    </Menu>
  );
};
